/* ──────────────────────────────────────────────────────────────
   Feeds & connectors — "Coming soon" preview.
   NOTE: The full Feeds & connectors implementation is preserved
   (commented out) below this component. Nothing was removed — to
   restore the old screen, delete this component and uncomment the
   block below.
   ────────────────────────────────────────────────────────────── */

import React from 'react';
import { View, Text, ScrollView, Platform, ViewStyle } from 'react-native';
import { useTheme } from '../../context/themeContext';
import { Icon } from '../../components/ui';

const FEATURES = [
  'Plug into your favourite platforms in a click',
  'Real-time feeds that stay perfectly in sync',
  'Set it once — it just works, everywhere',
];

export default function FeedsScreen() {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii, shadows, font } = theme;

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: colors.paper }}
      contentContainerStyle={{ padding: 24, paddingBottom: 64, flexGrow: 1, alignItems: 'center', justifyContent: 'center' }}
    >
      <View
        style={{
          width: '100%',
          maxWidth: 560,
          backgroundColor: colors.card,
          borderWidth: 1,
          borderColor: colors.line,
          borderRadius: radii.r4,
          paddingVertical: 40,
          paddingHorizontal: 32,
          alignItems: 'center',
          overflow: 'hidden',
          ...(shadows.lg as ViewStyle),
        }}
      >
        {/* ambient glow */}
        <View
          // @ts-ignore web radial glow
          style={Platform.OS === 'web'
            ? ({ position: 'absolute', top: -70, width: 300, height: 220, borderRadius: 150, background: `radial-gradient(circle, ${colors.success}26, transparent 70%)` } as any)
            : { position: 'absolute', top: -70, width: 300, height: 220, borderRadius: 150, backgroundColor: colors.successSoft }}
        />

        {/* icon ring */}
        <View
          // @ts-ignore web gradient chip
          style={{
            width: 68, height: 68, borderRadius: 20, alignItems: 'center', justifyContent: 'center',
            borderWidth: 1, borderColor: colors.successLine, marginBottom: 18,
            ...Platform.select({ web: { background: `linear-gradient(135deg, ${colors.success}33, ${colors.success}14)` } as any, default: { backgroundColor: colors.successSoft } }),
          }}
        >
          <Icon name="send" size={26} color={colors.success} />
        </View>

        {/* coming-soon badge */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 5, paddingHorizontal: 12, borderRadius: 999, backgroundColor: colors.successSoft, borderWidth: 1, borderColor: colors.successLine, marginBottom: 16 }}>
          <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: colors.success }} />
          <Text style={{ fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: colors.success, fontFamily: font.sans, letterSpacing: 0.5 }}>COMING SOON</Text>
        </View>

        <Text style={{ fontSize: fontSize['2xl'], fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: font.sans, textAlign: 'center', letterSpacing: -0.4 }}>
          Connect everything, effortlessly 🔌
        </Text>
        <Text style={{ fontSize: fontSize.md, color: colors.ink3, fontFamily: font.sans, textAlign: 'center', marginTop: 10, lineHeight: 22, maxWidth: 430 }}>
          Soon you'll sync your catalog everywhere your customers shop — with one-click connectors that keep every channel beautifully in sync. Less busywork, more growth.
        </Text>

        <View style={{ gap: 12, marginTop: 24, maxWidth: 380, width: '100%' }}>
          {FEATURES.map((f) => (
            <View key={f} style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <Icon name="check-circle" size={16} color={colors.success} />
              <Text style={{ flex: 1, fontSize: fontSize.sm, color: colors.ink2, fontFamily: font.sans }}>{f}</Text>
            </View>
          ))}
        </View>

        <Text style={{ fontSize: fontSize.xs, color: colors.ink4, fontFamily: font.sans, textAlign: 'center', marginTop: 26 }}>
          Everything connected, nothing in your way — coming soon to SK Catalog. ✨
        </Text>
      </View>
    </ScrollView>
  );
}


// ════════════════════════════════════════════════════════════════
// ORIGINAL FEEDS & CONNECTORS IMPLEMENTATION — preserved below, fully commented out.
// Nothing was removed. To restore: delete the preview component above
// and uncomment this block.
// ════════════════════════════════════════════════════════════════

// /* ──────────────────────────────────────────────────────────────
//    Distribution/Feeds/index.tsx
//    React Native + TypeScript — mirrors FeedsView exactly
//    ────────────────────────────────────────────────────────────── */
// 
// import React from 'react';
// import {
//   View,
//   Text,
//   ScrollView,
//   TouchableOpacity,
//   Platform,
//   useWindowDimensions,
//   ViewStyle,
//   TextStyle,
// } from 'react-native';
// 
// import { useTheme } from '../../context/themeContext';
// import { SK } from '../../data/mockData';
// import {
//   Icon,
//   Button,
//   Chip,
//   useToast,
// } from '../../components/ui';
// 
// // ── SectionTitle ──────────────────────────────────────────────
// 
// function SectionTitle({ children }: { children: string }) {
//   const { theme } = useTheme();
//   const { colors, fontSize, fontWeight } = theme;
//   return (
//     <Text
//       style={{
//         fontSize: fontSize.xl,
//         fontWeight: fontWeight.semibold,
//         color: colors.ink,
//         letterSpacing: -0.01 * fontSize.xl,
//         fontFamily: theme.font.sans,
//         marginTop: 28,
//         marginBottom: 14,
//         includeFontPadding: false,
//       }}
//     >
//       {children}
//     </Text>
//   );
// }
// 
// // ── ConnectorCard ─────────────────────────────────────────────
// 
// interface ConnectorCardProps {
//   emoji: string;
//   name: string;
//   status: string;
//   tone: 'success' | 'warn' | 'neutral';
//   onPress: () => void;
// }
// 
// function ConnectorCard({ emoji, name, status, tone, onPress }: ConnectorCardProps) {
//   const { theme } = useTheme();
//   const { colors, fontSize, fontWeight, radii, shadows } = theme;
// 
//   return (
//     <TouchableOpacity
//       onPress={onPress}
//       style={{
//         borderWidth: 1,
//         borderColor: colors.line,
//         borderRadius: radii.r3,
//         padding: 16,
//         backgroundColor: colors.card,
//         ...(shadows.sm as ViewStyle),
//         ...(Platform.select({
//           web: { cursor: 'pointer', transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.15s' } as any,
//         }) ?? {}),
//       }}
//       activeOpacity={0.85}
//     >
//       <Text style={{ fontSize: 28, marginBottom: 10 }}>{emoji}</Text>
//       <Text
//         style={{
//           fontWeight: fontWeight.semibold,
//           fontSize: fontSize.md,
//           color: colors.ink,
//           marginBottom: 6,
//           fontFamily: theme.font.sans,
//           includeFontPadding: false,
//         }}
//       >
//         {name}
//       </Text>
//       <Chip tone={tone} dot>{status}</Chip>
//     </TouchableOpacity>
//   );
// }
// 
// // ── Main screen ───────────────────────────────────────────────
// 
// const CONNECTORS = [
//   { n: 'Swiggy', e: '🛵', s: 'Connected', tone: 'success' as const },
//   { n: 'Meta Catalog', e: '📘', s: 'Connected', tone: 'success' as const },
//   { n: 'Zomato', e: '🍴', s: 'Staging', tone: 'warn' as const },
//   { n: 'Google Merchant', e: '🛒', s: 'Available', tone: 'neutral' as const },
// ];
// 
// export default function FeedsScreen() {
//   const { theme } = useTheme();
//   const { colors, fontSize, fontWeight, radii, shadows } = theme;
//   const toast = useToast();
//   const { width: winW } = useWindowDimensions();
//   const isMobile = winW < 820;
// 
//   const tableHeaderStyle: TextStyle = {
//     fontSize: fontSize.xs,
//     fontWeight: fontWeight.semibold,
//     color: colors.ink3,
//     textTransform: 'uppercase',
//     letterSpacing: 0.04 * fontSize.xs,
//     fontFamily: theme.font.sans,
//     includeFontPadding: false,
//     paddingVertical: 10,
//     paddingHorizontal: 12,
//   };
// 
//   return (
//     <ScrollView
//       style={{ flex: 1, backgroundColor: colors.paper }}
//       contentContainerStyle={{ padding: 24, paddingBottom: 64 }}
//       showsVerticalScrollIndicator={false}
//     >
//       {/* Page header */}
//       <View
//         style={{
//           flexDirection: 'row',
//           alignItems: 'flex-start',
//           justifyContent: 'space-between',
//           gap: 16,
//           marginBottom: 20,
//         }}
//       >
//         <View style={{ flex: 1 }}>
//           <Text
//             style={{
//               fontSize: fontSize['3xl'],
//               fontWeight: fontWeight.semibold,
//               color: colors.ink,
//               letterSpacing: -0.02 * fontSize['3xl'],
//               fontFamily: theme.font.sans,
//               includeFontPadding: false,
//             }}
//           >
//             Feeds & connectors
//           </Text>
//           <Text
//             style={{
//               fontSize: fontSize.md,
//               color: colors.ink3,
//               marginTop: 4,
//               fontFamily: theme.font.sans,
//               includeFontPadding: false,
//             }}
//           >
//             Exports and pluggable publish targets. Some connectors arrive in a later release.
//           </Text>
//         </View>
//         <Button kind="accent" icon="plus" onPress={() => toast.info('Add feed (demo)')}>
//           New feed
//         </Button>
//       </View>
// 
//       {/* Feeds table */}
//       <View
//         style={{
//           backgroundColor: colors.card,
//           borderWidth: 1,
//           borderColor: colors.line,
//           borderRadius: radii.r3,
//           overflow: 'hidden',
//           ...(shadows.sm as ViewStyle),
//         }}
//       >
//         {/* Table header — desktop only */}
//         {!isMobile && (
//           <View
//             style={{
//               flexDirection: 'row',
//               borderBottomWidth: 1,
//               borderBottomColor: colors.line,
//               backgroundColor: colors.paper2,
//             }}
//           >
//             <Text style={[tableHeaderStyle, { flex: 2 }]}>Feed</Text>
//             <Text style={[tableHeaderStyle, { minWidth: 80 }]}>Format</Text>
//             <Text style={[tableHeaderStyle, { flex: 3 }]}>URL</Text>
//             <Text style={[tableHeaderStyle, { minWidth: 60, textAlign: 'right' }]}>Items</Text>
//             <Text style={[tableHeaderStyle, { minWidth: 90 }]}>Refresh</Text>
//             <View style={{ width: 40 }} />
//           </View>
//         )}
// 
//         {/* Rows */}
//         {SK.feeds.map((f) => {
//           const formatChip = (
//             <Chip tone="neutral">
//               <Text style={{ fontFamily: theme.font.mono, fontSize: fontSize.xs, color: colors.neutral, includeFontPadding: false }}>{f.format}</Text>
//             </Chip>
//           );
//           const downloadBtn = (
//             <Button kind="ghost" size="sm" icon="download" iconOnly onPress={() => toast.success('Feed downloaded')} />
//           );
// 
//           // ── Mobile: stacked card ──
//           if (isMobile) {
//             return (
//               <View key={f.id} style={{ padding: 12, gap: 8, borderBottomWidth: 1, borderBottomColor: colors.line }}>
//                 <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
//                   <Text style={{ flex: 1, minWidth: 0, fontWeight: fontWeight.medium, fontSize: fontSize.md, color: colors.ink, fontFamily: theme.font.sans }} numberOfLines={1}>{f.name}</Text>
//                   {formatChip}
//                   {downloadBtn}
//                 </View>
//                 {!!f.url && (
//                   <Text style={{ fontFamily: theme.font.mono, fontSize: fontSize.xs, color: colors.ink3 }} numberOfLines={1}>{f.url}</Text>
//                 )}
//                 <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: theme.font.sans }}>
//                   {f.items} items · refresh {f.refresh}
//                 </Text>
//               </View>
//             );
//           }
// 
//           // ── Desktop: table row ──
//           return (
//             <View key={f.id} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 10, paddingHorizontal: 12, borderBottomWidth: 1, borderBottomColor: colors.line, gap: 12 }}>
//               <Text style={{ flex: 2, fontWeight: fontWeight.medium, fontSize: fontSize.md, color: colors.ink, fontFamily: theme.font.sans, includeFontPadding: false }}>{f.name}</Text>
//               <View style={{ minWidth: 80 }}>{formatChip}</View>
//               <Text style={{ flex: 3, fontFamily: theme.font.mono, fontSize: fontSize.sm, color: colors.ink3, includeFontPadding: false }} numberOfLines={1}>{f.url}</Text>
//               <Text style={{ minWidth: 60, textAlign: 'right', fontFamily: theme.font.mono, fontSize: fontSize.sm, color: colors.ink, includeFontPadding: false }}>{f.items}</Text>
//               <Text style={{ minWidth: 90, fontSize: fontSize.sm, color: colors.ink3, fontFamily: theme.font.sans, includeFontPadding: false }}>{f.refresh}</Text>
//               <View style={{ width: 40, alignItems: 'center' }}>{downloadBtn}</View>
//             </View>
//           );
//         })}
//       </View>
// 
//       {/* Connectors section */}
//       <SectionTitle>Connectors</SectionTitle>
// 
//       {/* tpl-grid: auto-fill minmax 200px */}
//       <View
//         style={
//           Platform.OS === 'web'
//             ? ({
//                 display: 'grid',
//                 gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
//                 gap: 14,
//               } as any)
//             : {
//                 flexDirection: 'row',
//                 flexWrap: 'wrap',
//                 gap: 14,
//               }
//         }
//       >
//         {CONNECTORS.map((c) => (
//           <View
//             key={c.n}
//             style={
//               Platform.OS !== 'web'
//                 ? { width: '47%' }
//                 : undefined
//             }
//           >
//             <ConnectorCard
//               emoji={c.e}
//               name={c.n}
//               status={c.s}
//               tone={c.tone}
//               onPress={() => toast.info(`${c.n} connector`)}
//             />
//           </View>
//         ))}
//       </View>
//     </ScrollView>
//   );
// }
